<template>
  <FormulateForm class="mc-basic-info-request" v-model="formValues">
    <h4 class="mc-basic-info-request__title card-title">{{ $t('delivery.basicInformation') }}</h4>

    <div class="card">
      <div class="card-body">
        <h4 class="mc-basic-info-request__title card-title">{{ $t('deliveryRequest.tabs.delivery') }}</h4>

        <b-row>
          <b-col cols="12" xl="6">
            <DeliveryReadOnly
              class="mc-basic-info-request__read-only"
              :showResponse="showResponse"
            />
          </b-col>
        </b-row>

        <div class="d-flex w-100 justify-content-between"></div>

        <b-row>
          <b-col md="4" lg="3" v-for="field in basicInfoFields" :key="field.name">
            <FormulateInput class="p-0" v-bind="field" disabled />
          </b-col>
        </b-row>

        <McToggle
          v-model="isSwitched"
          class="mc-basic-info-request__toggle"
          :label="$t('deliveryRequest.detail.otherInfo')"
          placement="right"
        />

        <b-row v-show="isSwitched">
          <b-col cols="12" md="6" class="d-flex flex-column gap-4">
            <template v-for="field in otherInfoFields">
              <FormulateInput
                :key="field.name"
                disabled
                class="p-0"
                v-bind="field"
                :fullObject.sync="fullObjects[field.name]"
              />
            </template>
          </b-col>
        </b-row>

        <hr />

        <b-row>
          <b-col cols="12" md="6">
            <h4 class="mc-basic-info-request__title card-title">{{ $t('delivery.originLocation') }}</h4>

            <AddressReadOnly
              class="mc-basic-info-request__read-only"
              type="parent_origin_address"
              :showResponse="showResponse"
            />
            <b-row>
              <b-col cols="12" md="6" class="d-flex flex-column gap-4">
                <template v-for="field in originAddressFields">
                  <FormulateInput
                    :key="field.name"
                    disabled
                    class="p-0"
                    v-bind="field"
                    :fullObject="fullObjects[field.name]"
                  />
                </template>
              </b-col>
            </b-row>
          </b-col>

          <b-col cols="12" md="6">
            <h4 class="mc-basic-info-request__title card-title">{{ $t('delivery.destinationLocation') }}</h4>

            <AddressReadOnly
              class="mc-basic-info-request__read-only"
              type="parent_destination_address"
              :showResponse="showResponse"
            />
            <b-row>
              <b-col cols="12" md="6" class="d-flex flex-column gap-4">
                <template v-for="field in destinationAddressFields">
                  <FormulateInput
                    :key="field.name"
                    disabled
                    class="p-0"
                    v-bind="field"
                    :fullObject="fullObjects[field.name]"
                  />
                </template>
              </b-col>
            </b-row>
          </b-col>
        </b-row>

        <hr />

        <h4 class="mc-basic-info-request__title card-title">{{ $t('global.object.fleet') }}</h4>
        <b-row class="align-items-end">
          <b-col cols="12" md="3">
            <FormulateInput
              disabled
              class="p-0"
              v-bind="fleetFields"
              :fullObject.sync="fullObjects[fleetFields.name]"
            />
          </b-col>
          <b-col sm="12" md="3">
            <BoxCapacityReadOnly :boxCapacity="boxCapacity" />
          </b-col>
        </b-row>
      </div>
    </div>
  </FormulateForm>
</template>

<script>
import McToggle from '@/components/McToggle'
import DeliveryReadOnly from '../components/ReadOnly/DeliveryReadOnly'
import AddressReadOnly from '../components/ReadOnly/AddressReadOnly'
import BoxCapacityReadOnly from '../components/ReadOnly/BoxCapacityReadOnly'

import {
  basicInfoFields,
  otherInfoFields,
  originAddressFields,
  destinationAddressFields,
  fleetFields,
} from '../lib/fields/detail-fields'

import get from 'lodash/get'
import moment from 'moment'

moment.locale('id')

export default {
  components: {
    McToggle,
    DeliveryReadOnly,
    AddressReadOnly,
    BoxCapacityReadOnly,
  },
  props: {
    showResponse: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      isSwitched: true,
      boxCapacity: null,

      formValues: {},
      fullObjects: {
        parent_customer_notes: [],
        parent_origin_address: {},
        parent_destination_address: {},
        parent_box_type: {},
      },
    }
  },
  computed: {
    basicInfoFields() {
      return basicInfoFields(this.$t.bind(this))
    },
    otherInfoFields() {
      return otherInfoFields(this.$t.bind(this))
    },
    originAddressFields() {
      return originAddressFields(this.$t.bind(this))
    },
    destinationAddressFields() {
      return destinationAddressFields(this.$t.bind(this))
    },
    fleetFields() {
      return fleetFields(this.$t.bind(this))
    },
    fields() {
      return [
        ...this.basicInfoFields,
        ...this.otherInfoFields,
        ...this.originAddressFields,
        ...this.destinationAddressFields,
        this.fleetFields,
      ]
    },
  },
  watch: {
    showResponse(newValue) {
      this.assignShowData(newValue)
    },
  },
  methods: {
    assignShowData(data) {
      this.fields.forEach((field) => {
        let value = get(data, field.key)

        if (field.type == 'date-picker' && value != '') {
          value = moment(value).format('YYYY-MM-DDTHH:mm:ss.SSSZ')
        }

        this.$set(this.formValues, field.name, value)

        if (['value-help-v2', 'select-resourceful-v2'].includes(field.type)) {
          value = data[field.name]

          if (field.name == 'parent_customer_notes') {
            value = data[field.name].map((item, idx) => {
              return {
                id: idx,
                note: item,
              }
            })
          }

          this.$set(this.fullObjects, field.name, value)
        }
      })

      this.boxCapacity = data.parent_box_type.weight_capacity
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-basic-info-request {
  &__title {
    margin-bottom: 12px;
  }

  &__read-only {
    margin-bottom: 24px;
  }

  &__toggle {
    margin: 20px 0;
  }

  hr {
    margin: 24px 0;
  }
}
</style>
